import {
  type RumEvent,
  type RumEventDomainContext,
} from '@datadog/browser-rum';

import { IGNORED_MESSAGE_PHRASES, IGNORED_ORIGINS } from './constants';
import { createKeywordMatcher } from './filterLog';

const ALLOWED_RESOURCES = ['depop', 'ctfassets', 'cloudfront'];

const messageIsIgnored = createKeywordMatcher(IGNORED_MESSAGE_PHRASES);
const originIsIgnored = createKeywordMatcher(IGNORED_ORIGINS);
const resourceIsAllowed = createKeywordMatcher(ALLOWED_RESOURCES);

type RequestError = {
  status: number;
  statusText?: string;
  url?: string;
};

// A Response object's properties are not enumerable, so they have to be
// copied out individually to survive being serialised onto the event.
function readRequestError(
  context: RumEventDomainContext
): RequestError | undefined {
  const response = (context as { error?: { response?: Response } })?.error
    ?.response;

  if (typeof response?.status !== 'number') {
    return undefined;
  }

  return {
    status: response.status,
    ...(response.statusText && { statusText: response.statusText }),
    ...(response.url && { url: response.url }),
  };
}

function shouldDiscardRumEvent(event: RumEvent) {
  if (window?.navigator?.webdriver) {
    return true;
  }

  if (event.type === 'error') {
    if (messageIsIgnored(event.error.message)) {
      return true;
    }

    // Errors captured from a console call carry no stack; the file that
    // logged them is named on handling_stack instead.
    const origin = [event.error.stack, event.error.handling_stack]
      .filter(Boolean)
      .join('\n');

    if (originIsIgnored(origin)) {
      return true;
    }
  }

  if (event.type === 'resource' && !resourceIsAllowed(event.resource.url)) {
    return true;
  }

  return false;
}

export function beforeSendRumEvent(
  event: RumEvent,
  context: RumEventDomainContext
) {
  if (shouldDiscardRumEvent(event)) {
    return false;
  }

  const requestError = readRequestError(context);

  if (requestError) {
    event.context = { ...event.context, requestError };
  }

  return true;
}
